import React, { useEffect } from "react";
import { X } from "lucide-react";

// Small "pick an action" dialog (Template / Import buttons): icon header,
// bullet notes, action buttons. Same look as the Process Payroll modal.
const PayrollChoiceModal = ({
  icon: Icon,
  title,
  subtitle,
  points = [],
  actions = [],
  onClose,
  closeLabel,
  theme,
  isRTL,
  width = 640,
}) => {
  const isDark = theme === "dark";
  const c = {
    primary: "#2e7cba",
    primarySoft: isDark ? "rgba(46,124,186,0.18)" : "#eef5fb",
    success: "#059669",
    textPrimary: isDark ? "#f1f5f9" : "#0f172a",
    textSecondary: isDark ? "#cbd5e1" : "#475569",
    textMuted: isDark ? "#94a3b8" : "#64748b",
    border: isDark ? "#475569" : "#cbd5e1",
    borderLight: isDark ? "#334155" : "#e2e8f0",
    surface: isDark ? "#1e293b" : "#ffffff",
    surfaceSubtle: isDark ? "#162033" : "#f8fafc",
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const buttonStyle = (variant) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    height: "42px",
    padding: "0 18px",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    ...(variant === "primary" && { background: c.primary, color: "#ffffff", border: `1px solid ${c.primary}` }),
    ...(variant === "success" && { background: c.success, color: "#ffffff", border: `1px solid ${c.success}` }),
    ...(variant === "secondary" && { background: c.surface, color: c.textPrimary, border: `1px solid ${c.border}` }),
  });

  return (
    <div
      className="modal-overlay payroll-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99990,
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <style>{`
        .pcm-btn:hover { filter: brightness(0.94); }
        .pcm-btn-secondary:hover { background: ${c.surfaceSubtle} !important; }
        .pcm-close:hover { background: ${c.surfaceSubtle} !important; color: ${c.textPrimary} !important; }
        @media (max-width: 640px) { .pcm-actions > button { flex: 1 1 100%; } }
      `}</style>
      <div
        role="dialog"
        aria-modal="true"
        dir={isRTL ? "rtl" : "ltr"}
        style={{
          width: `min(${width}px, 100%)`,
          maxHeight: "calc(100vh - 32px)",
          display: "flex",
          flexDirection: "column",
          borderRadius: "18px",
          background: c.surface,
          color: c.textPrimary,
          border: `1px solid ${c.borderLight}`,
          boxShadow: "0 24px 64px rgba(15, 23, 42, 0.28)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            padding: "20px 24px",
            borderBottom: `1px solid ${c.borderLight}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0 }}>
            {Icon && (
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "12px",
                  background: c.primarySoft,
                  color: c.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon size={22} />
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: c.textPrimary }}>{title}</h2>
              {subtitle && (
                <div style={{ fontSize: "13.5px", color: c.textMuted, marginTop: "2px" }}>{subtitle}</div>
              )}
            </div>
          </div>
          <button
            type="button"
            className="pcm-close"
            onClick={onClose}
            aria-label={closeLabel}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: c.textMuted,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "background 0.15s",
            }}
          >
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        {points.length > 0 && (
          <div style={{ padding: "20px 24px", overflowY: "auto", background: c.surfaceSubtle }}>
            <ul style={{ margin: 0, paddingInlineStart: "20px", display: "flex", flexDirection: "column", gap: "10px" }}>
              {points.map((p, i) => (
                <li key={i} style={{ fontSize: "14px", lineHeight: 1.7, color: c.textSecondary }}>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div
          className="pcm-actions"
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            gap: "10px",
            padding: "16px 24px",
            borderTop: `1px solid ${c.borderLight}`,
          }}
        >
          {actions.map((a) => {
            const ActionIcon = a.icon;
            const variant = a.variant || "secondary";
            return (
              <button
                key={a.key}
                type="button"
                className={`pcm-btn${variant === "secondary" ? " pcm-btn-secondary" : ""}`}
                onClick={a.onClick}
                style={buttonStyle(variant)}
              >
                {ActionIcon && <ActionIcon size={17} />}
                {a.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PayrollChoiceModal;
